// petz-cart.js — Cart page progressive enhancement on Shopify's standard storefront
// actions. No jQuery, no framework, no fetch patching, no local price maths.
//
// The Cart page is fully functional without this file: quantity updates post through
// updates[], removal follows item.url_to_remove, the note posts through name="note" and
// checkout posts through name="checkout". Everything here is layered on top.
//
// Contract (shopify.dev):
//   Shopify.actions.updateCart(payload, options) -> Promise<{cart, userErrors, warnings}>
//   Shopify.actions.updateCart.configure({ eventTarget, handler }) -> false if already owned
//   Shopify.actions.getCart() -> Promise<{cart}>  (cart.discountCodes = [{code, applicable}])
//   CartLineInput.id accepts the AJAX cart line key, which is what item.key renders.
//   Section Rendering API: <url>?sections=a,b,c  (maximum five)
//
// Shopify stays authoritative for every price, discount, total and cart count. This file
// only asks Shopify to change the cart and then re-renders Shopify's own markup.

(function () {
  'use strict';

  var ROOT_SEL = '[data-petz-cart]';
  var DEBOUNCE_MS = 500;
  var MAX_RECOMMENDATION_SOURCES = 3;

  var enhanced = false;
  var controllers = {};
  var debounceTimers = {};
  var appliedCodes = [];
  var recsLoading = false;
  var stockNotices = {};

  // ─── Small helpers ───────────────────────────────────────────────────────────

  function root() {
    return document.querySelector(ROOT_SEL);
  }

  function closestFrom(node, selector) {
    if (!node || typeof node.closest !== 'function') return null;
    return node.closest(selector);
  }

  function str(name) {
    var el = root();
    if (!el) return '';
    return el.getAttribute('data-str-' + name) || '';
  }

  function unique(list) {
    var seen = {};
    var out = [];
    list.forEach(function (value) {
      if (!value || seen[value]) return;
      seen[value] = true;
      out.push(value);
    });
    return out;
  }

  // Reuses the global live regions from snippets/accessibility-announcers.liquid.
  // Clearing first guarantees a repeated identical message is announced again.
  function announce(message, urgent) {
    if (!message) return;
    var selector = urgent ? '[data-petz-accessibility-alert]' : '[data-petz-accessibility-status]';
    var region = document.querySelector(selector);
    if (!region) return;
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 50);
  }

  function findLine(lineId) {
    var lines = document.querySelectorAll('[data-petz-line]');
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].getAttribute('data-line-id') === lineId) return lines[i];
    }
    return null;
  }

  function cartProductIds() {
    var ids = [];
    var lines = document.querySelectorAll('[data-petz-line]');
    for (var i = 0; i < lines.length; i++) {
      ids.push(lines[i].getAttribute('data-product-id'));
    }
    return unique(ids);
  }

  // Only the line being changed is disabled, so the rest of the cart stays usable.
  function setBusy(line, busy) {
    if (!line) return;
    line.setAttribute('aria-busy', busy ? 'true' : 'false');
    var controls = line.querySelectorAll('[data-petz-qty-step], [data-petz-qty-input]');
    for (var i = 0; i < controls.length; i++) {
      if (busy) {
        controls[i].setAttribute('disabled', 'disabled');
      } else {
        controls[i].removeAttribute('disabled');
      }
    }
    var remove = line.querySelector('[data-petz-remove]');
    if (remove) remove.setAttribute('aria-disabled', busy ? 'true' : 'false');
  }

  // ─── Stock-cap notices ───────────────────────────────────────────────────────
  // Shopify accepts a smaller quantity than requested and reports it as a warning
  // rather than an error, so the line still updates and the buyer needs a visible
  // explanation. State is kept per line id and re-applied after every section
  // re-render, so one line's notice is never written onto another line and is
  // never lost when Shopify's markup is swapped in.

  function stockCapQuantity(result) {
    var warnings = (result && result.warnings) || [];
    var warning = null;
    warnings.forEach(function (candidate) {
      if (candidate && candidate.code === 'MERCHANDISE_NOT_ENOUGH_STOCK') warning = candidate;
    });
    if (!warning) return null;

    // shopify.dev documents CartSummary.lines as CartLine[]; this storefront
    // currently returns the connection form { nodes: CartLine[] }. Both are
    // supported so the official contract keeps working if the runtime shape
    // changes, and neither is treated as replacing the other.
    var reportedLines = result && result.cart && result.cart.lines;
    var lines = Array.isArray(reportedLines)
      ? reportedLines
      : reportedLines && Array.isArray(reportedLines.nodes)
        ? reportedLines.nodes
        : [];

    // warning.target is the Cart line id of the capped line. Only an exact id
    // match with a usable quantity is accepted - never a guess.
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      if (line && line.id === warning.target && Number.isFinite(line.quantity)) {
        return line.quantity;
      }
    }
    return null;
  }

  function stockMessage(count) {
    var template = str('stock-capped');
    if (!template) return '';
    return template.replace('__COUNT__', String(count));
  }

  function paintStockNotice(line) {
    if (!line) return;
    var notice = line.querySelector('[data-petz-stock-notice]');
    var input = line.querySelector('[data-petz-qty-input]');
    if (!notice) return;

    var lineId = line.getAttribute('data-line-id');
    var count = Object.prototype.hasOwnProperty.call(stockNotices, lineId) ? stockNotices[lineId] : null;

    if (count === null) {
      notice.textContent = '';
      notice.hidden = true;
      if (input) {
        var base = input.getAttribute('data-describedby-base');
        if (base) input.setAttribute('aria-describedby', base);
      }
      return;
    }

    notice.textContent = stockMessage(count);
    notice.hidden = false;

    if (input && notice.id) {
      if (!input.getAttribute('data-describedby-base')) {
        input.setAttribute('data-describedby-base', input.getAttribute('aria-describedby') || '');
      }
      var original = input.getAttribute('data-describedby-base') || '';
      if (original.indexOf(notice.id) === -1) {
        input.setAttribute('aria-describedby', (original + ' ' + notice.id).trim());
      }
    }
  }

  // Re-applies every remembered notice onto the freshly rendered lines, and drops
  // entries whose line no longer exists (removed line, emptied cart).
  function applyStockNotices() {
    Object.keys(stockNotices).forEach(function (lineId) {
      if (!findLine(lineId)) delete stockNotices[lineId];
    });
    var lines = document.querySelectorAll('[data-petz-line]');
    for (var i = 0; i < lines.length; i++) paintStockNotice(lines[i]);
  }

  function setStockNotice(lineId, count) {
    if (count === null) {
      delete stockNotices[lineId];
    } else {
      stockNotices[lineId] = count;
    }
    paintStockNotice(findLine(lineId));
  }

  function clearDebounce(lineId) {
    if (!lineId || !debounceTimers[lineId]) return;
    window.clearTimeout(debounceTimers[lineId]);
    delete debounceTimers[lineId];
  }

  function markEnhanced() {
    var el = root();
    if (el) el.setAttribute('data-petz-cart-enhanced', '');
  }

  // ─── Section rendering ───────────────────────────────────────────────────────

  function headerSectionId() {
    var cartLink = document.querySelector('.petz-header__cart');
    if (!cartLink) return null;
    var wrapper = closestFrom(cartLink, '[id^="shopify-section-"]');
    if (!wrapper) return null;
    return wrapper.id.replace(/^shopify-section-/, '');
  }

  // Inside a JSON template, Liquid's section.id is the template key ("main"),
  // while the Section Rendering API needs the rendered wrapper id
  // ("template--1234__main"). The wrapper is the only reliable source.
  function mainSectionId() {
    var el = root();
    if (!el) return null;
    var wrapper = closestFrom(el, '[id^="shopify-section-"]');
    if (wrapper) return wrapper.id.replace(/^shopify-section-/, '');
    return el.getAttribute('data-section-id');
  }

  function sectionsUrl(ids) {
    var url = new URL(window.location.href);
    url.searchParams.set('sections', ids.join(','));
    return url.toString();
  }

  // Captures the things a server re-render would otherwise throw away: which
  // disclosure was open, where focus was, and what was typed but not yet applied.
  function captureState() {
    var state = { disclosures: [], focus: null, value: null, caret: null };

    var details = document.querySelectorAll('[data-petz-disclosure]');
    for (var i = 0; i < details.length; i++) {
      if (details[i].open) state.disclosures.push(details[i].getAttribute('data-petz-disclosure'));
    }

    var active = document.activeElement;
    if (!active || !active.getAttribute) return state;

    var line = closestFrom(active, '[data-petz-line]');
    if (active.hasAttribute('data-petz-qty-input') && line) {
      state.focus = { type: 'qty', lineId: line.getAttribute('data-line-id') };
      state.value = active.value;
    } else if (active.hasAttribute('data-petz-qty-step') && line) {
      state.focus = {
        type: 'step',
        lineId: line.getAttribute('data-line-id'),
        step: active.getAttribute('data-petz-qty-step')
      };
    } else if (active.hasAttribute('data-petz-discount-input')) {
      state.focus = { type: 'discount' };
      state.value = active.value;
      state.caret = active.selectionStart;
    }

    return state;
  }

  function restoreState(state) {
    if (!state) return;

    state.disclosures.forEach(function (name) {
      var el = document.querySelector('[data-petz-disclosure="' + name + '"]');
      if (el) el.open = true;
    });

    if (!state.focus) return;

    if (state.focus.type === 'discount') {
      var input = document.querySelector('[data-petz-discount-input]');
      if (!input) return;
      if (state.value !== null) input.value = state.value;
      input.focus();
      if (state.caret !== null && input.setSelectionRange) {
        try {
          input.setSelectionRange(state.caret, state.caret);
        } catch (e) {
          /* setSelectionRange is unsupported on some input types */
        }
      }
      return;
    }

    var line = findLine(state.focus.lineId);
    if (!line) return;

    if (state.focus.type === 'qty') {
      var qty = line.querySelector('[data-petz-qty-input]');
      if (qty) qty.focus();
      return;
    }

    var buttons = line.querySelectorAll('[data-petz-qty-step]');
    for (var i = 0; i < buttons.length; i++) {
      if (buttons[i].getAttribute('data-petz-qty-step') === state.focus.step) {
        buttons[i].focus();
        return;
      }
    }
  }

  // Re-renders Shopify's own markup rather than mutating numbers in the browser.
  function refreshSections() {
    var ids = [];
    var main = mainSectionId();
    var header = headerSectionId();
    if (main) ids.push(main);
    if (header) ids.push(header);
    if (!ids.length) return Promise.resolve();

    // No Accept header: Shopify's /cart route content-negotiates, and asking for
    // application/json returns the AJAX cart payload instead of the sections.
    return fetch(sectionsUrl(ids))
      .then(function (response) {
        if (!response.ok) throw new Error('sections ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var state = captureState();

        if (main && typeof data[main] === 'string') {
          var host = document.getElementById('shopify-section-' + main);
          if (host) host.outerHTML = data[main];
          markEnhanced();
        }

        if (header && typeof data[header] === 'string') updateHeaderCart(data[header]);

        restoreState(state);
        renderAppliedCodes();
        applyStockNotices();
        loadRecommendations();
      });
  }

  // The Header is owned by another section and runs its own navigation JavaScript,
  // so only the cart count and its accessible name are replaced - both taken from
  // Shopify's fresh render of that section, never counted in the browser.
  function updateHeaderCart(html) {
    var live = document.querySelector('.petz-header__cart');
    if (!live) return;

    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.querySelector('.petz-header__cart');
    if (!fresh) return;

    var freshCount = fresh.querySelector('.petz-header__cart-count');
    var liveCount = live.querySelector('.petz-header__cart-count');
    if (freshCount && liveCount) liveCount.textContent = freshCount.textContent;

    var label = fresh.getAttribute('aria-label');
    if (label) live.setAttribute('aria-label', label);
  }

  // ─── Cart line quantity ──────────────────────────────────────────────────────

  function handleResult(result, successMessage) {
    var errors = (result && result.userErrors) || [];
    if (errors.length) {
      announce(errors[0].message || str('error'), true);
      return false;
    }

    var warnings = (result && result.warnings) || [];
    if (warnings.length) {
      announce(warnings[0].message || str('updated'), false);
      return true;
    }

    announce(successMessage, false);
    return true;
  }

  function setQuantity(lineId, quantity) {
    if (!lineId) return;

    // A newer request for the same line supersedes the one in flight.
    if (controllers[lineId]) controllers[lineId].abort();
    var controller = new AbortController();
    controllers[lineId] = controller;

    var line = findLine(lineId);
    setBusy(line, true);
    announce(str('updating'), false);

    var message = quantity === 0 ? str('removed') : str('updated');

    window.Shopify.actions
      .updateCart(
        { lines: [{ id: lineId, quantity: quantity }] },
        { signal: controller.signal, event: { context: 'cart' } }
      )
      .then(function (result) {
        var errors = (result && result.userErrors) || [];
        if (errors.length) {
          announce(errors[0].message || str('error'), true);
          return;
        }

        // Shopify capped the line: it accepted a smaller quantity and said so.
        // The accepted quantity comes from Shopify's own response, never from an
        // inventory read or a browser-side guess.
        var capped = stockCapQuantity(result);
        setStockNotice(lineId, capped);

        if (capped !== null) {
          // One announcement only, localized, replacing the generic warning text.
          announce(stockMessage(capped), false);
          return;
        }

        handleResult(result, message);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        announce(str('error'), true);
        // Server truth restores the last accepted quantity.
        return refreshSections().catch(function () {});
      })
      .then(function () {
        if (controllers[lineId] === controller) delete controllers[lineId];
        setBusy(findLine(lineId), false);
      });
  }

  function stepQuantity(button) {
    var line = closestFrom(button, '[data-petz-line]');
    if (!line || line.getAttribute('aria-busy') === 'true') return;

    var input = line.querySelector('[data-petz-qty-input]');
    if (!input) return;

    var delta = parseInt(button.getAttribute('data-petz-qty-step'), 10) || 0;
    var next = (parseInt(input.value, 10) || 0) + delta;
    if (next < 0) next = 0;

    input.value = String(next);

    var lineId = line.getAttribute('data-line-id');
    clearDebounce(lineId);
    setQuantity(lineId, next);
  }

  function commitInput(input) {
    var line = closestFrom(input, '[data-petz-line]');
    if (!line) return;
    var lineId = line.getAttribute('data-line-id');
    clearDebounce(lineId);
    var value = parseInt(input.value, 10);
    if (isNaN(value) || value < 0) return;
    setQuantity(lineId, value);
  }

  // ─── Order note ──────────────────────────────────────────────────────────────
  // Hiding the manual "Update cart" submit would otherwise leave a JavaScript
  // visitor no way to save the note before checkout, so the note is saved through
  // the same standard action. It is saved on change rather than on every
  // keystroke, so a re-render never lands while the field is being typed in.

  function saveNote(textarea) {
    var value = textarea.value;
    announce(str('updating'), false);

    window.Shopify.actions
      .updateCart({ note: value }, { event: { context: 'cart' } })
      .then(function (result) {
        handleResult(result, str('note-saved'));
      })
      .catch(function () {
        announce(str('error'), true);
      });
  }

  // ─── Discount codes ──────────────────────────────────────────────────────────

  function setStatus(message, state) {
    var el = document.querySelector('[data-petz-discount-status]');
    if (!el) return;
    el.textContent = message || '';
    if (state) {
      el.setAttribute('data-state', state);
    } else {
      el.removeAttribute('data-state');
    }
  }

  // Only codes Shopify reports as applicable are presented as applied.
  function renderAppliedCodes() {
    var list = document.querySelector('[data-petz-discount-applied]');
    if (!list) return;

    list.textContent = '';

    appliedCodes.forEach(function (entry) {
      if (!entry || !entry.code || entry.applicable === false) return;

      var item = document.createElement('li');
      item.className = 'petz-cart__applied-code';

      var label = document.createElement('span');
      label.textContent = entry.code;

      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'petz-cart__applied-remove';
      remove.setAttribute('data-petz-discount-remove', entry.code);
      remove.setAttribute('aria-label', str('remove-code') + ': ' + entry.code);
      remove.textContent = '×';

      item.appendChild(label);
      item.appendChild(remove);
      list.appendChild(item);
    });
  }

  // discountCodes is the complete set, so applying means sending the current set
  // plus the new code, and removing means sending the set without it.
  function submitDiscountCodes(codes, enteredCode) {
    var apply = document.querySelector('[data-petz-discount-apply]');
    if (apply) apply.disabled = true;
    announce(str('updating'), false);

    return window.Shopify.actions
      .updateCart({ discountCodes: codes }, { event: { context: 'cart' } })
      .then(function (result) {
        var errors = (result && result.userErrors) || [];
        if (errors.length) {
          var text = errors[0].message || str('error');
          setStatus(text, 'error');
          announce(text, true);
          return;
        }

        appliedCodes = (result && result.cart && result.cart.discountCodes) || [];
        renderAppliedCodes();

        if (!enteredCode) {
          setStatus(str('discount-removed'), 'success');
          announce(str('discount-removed'), false);
          return;
        }

        var match = null;
        appliedCodes.forEach(function (entry) {
          if (!entry || !entry.code) return;
          if (String(entry.code).toLowerCase() === String(enteredCode).toLowerCase()) match = entry;
        });

        if (!match || match.applicable === false) {
          setStatus(str('discount-invalid'), 'error');
          announce(str('discount-invalid'), true);
          return;
        }

        setStatus(str('discount-applied'), 'success');
        announce(str('discount-applied'), false);
        var input = document.querySelector('[data-petz-discount-input]');
        if (input) input.value = '';
      })
      .catch(function () {
        setStatus(str('error'), 'error');
        announce(str('error'), true);
      })
      .then(function () {
        var button = document.querySelector('[data-petz-discount-apply]');
        if (button) button.disabled = false;
      });
  }

  function applyDiscount(rawValue) {
    var code = (rawValue || '').trim();
    if (!code) {
      setStatus(str('discount-empty'), 'error');
      return;
    }

    var codes = appliedCodes.map(function (entry) {
      return entry.code;
    });
    if (codes.indexOf(code) === -1) codes.push(code);

    submitDiscountCodes(codes, code);
  }

  function removeDiscount(target) {
    var codes = appliedCodes
      .map(function (entry) {
        return entry.code;
      })
      .filter(function (code) {
        return code !== target;
      });

    submitDiscountCodes(codes, null);
  }

  // Liquid exposes no manually entered codes on the cart object, so the applied
  // set is read from Shopify through getCart on load.
  function seedDiscountCodes() {
    var getCart = window.Shopify.actions.getCart;
    if (typeof getCart !== 'function') return;

    Promise.resolve(getCart())
      .then(function (result) {
        appliedCodes = (result && result.cart && result.cart.discountCodes) || [];
        renderAppliedCodes();
      })
      .catch(function () {
        /* Seeding is best effort; the field still works without it. */
      });
  }

  // ─── Complementary recommendations ───────────────────────────────────────────

  function loadRecommendations() {
    var host = document.querySelector('[data-petz-cart-recs]');
    if (!host || recsLoading) return;

    var list = host.querySelector('[data-petz-cart-recs-list]');
    if (!list) return;

    var inCart = cartProductIds();
    if (!inCart.length) {
      list.textContent = '';
      host.hidden = true;
      return;
    }

    var limit = parseInt(host.getAttribute('data-limit'), 10) || 2;
    if (limit > 2) limit = 2;
    if (limit < 1) limit = 1;

    var base = host.getAttribute('data-recommendations-url');
    var sectionFile = host.getAttribute('data-section-file');
    if (!base || !sectionFile) return;

    recsLoading = true;

    var sources = inCart.slice(0, MAX_RECOMMENDATION_SOURCES);
    var requests = sources.map(function (productId) {
      var url = new URL(base, window.location.origin);
      url.searchParams.set('section_id', sectionFile);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('intent', 'complementary');
      url.searchParams.set('limit', String(limit));
      return fetch(url.toString())
        .then(function (response) {
          return response.ok ? response.text() : '';
        })
        .catch(function () {
          return '';
        });
    });

    Promise.all(requests)
      .then(function (documents) {
        // A product already in the cart, or already picked, is never offered.
        var seen = {};
        inCart.forEach(function (id) {
          seen[id] = true;
        });

        var picked = [];
        documents.forEach(function (html) {
          if (!html) return;
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var items = parsed.querySelectorAll('[data-petz-rec-item]');
          for (var i = 0; i < items.length; i++) {
            if (picked.length >= limit) return;
            var productId = items[i].getAttribute('data-product-id');
            if (!productId || seen[productId]) continue;
            seen[productId] = true;
            picked.push(items[i]);
          }
        });

        list.textContent = '';
        picked.forEach(function (item) {
          list.appendChild(document.importNode(item, true));
        });
        host.hidden = picked.length === 0;
      })
      .catch(function () {
        host.hidden = true;
      })
      .then(function () {
        recsLoading = false;
      });
  }

  function addRecommendation(button) {
    if (button.disabled) return;
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    button.disabled = true;
    announce(str('updating'), false);

    window.Shopify.actions
      .updateCart(
        { lines: [{ merchandiseId: variantId, quantity: 1 }] },
        { event: { context: 'cart' } }
      )
      .then(function (result) {
        handleResult(result, str('updated'));
      })
      .catch(function () {
        announce(str('error'), true);
        return refreshSections().catch(function () {});
      });
  }

  // ─── Delegated events ────────────────────────────────────────────────────────
  // Delegation keeps every control working after a section is replaced, without
  // rebinding anything.

  document.addEventListener('click', function (event) {
    if (!enhanced) return;

    var step = closestFrom(event.target, '[data-petz-qty-step]');
    if (step) {
      event.preventDefault();
      stepQuantity(step);
      return;
    }

    var remove = closestFrom(event.target, '[data-petz-remove]');
    if (remove) {
      var line = closestFrom(remove, '[data-petz-line]');
      if (!line) return;
      event.preventDefault();
      if (line.getAttribute('aria-busy') === 'true') return;
      clearDebounce(line.getAttribute('data-line-id'));
      setQuantity(line.getAttribute('data-line-id'), 0);
      return;
    }

    var apply = closestFrom(event.target, '[data-petz-discount-apply]');
    if (apply) {
      event.preventDefault();
      var input = document.querySelector('[data-petz-discount-input]');
      applyDiscount(input ? input.value : '');
      return;
    }

    var removeCode = closestFrom(event.target, '[data-petz-discount-remove]');
    if (removeCode) {
      event.preventDefault();
      removeDiscount(removeCode.getAttribute('data-petz-discount-remove'));
      return;
    }

    var add = closestFrom(event.target, '[data-petz-rec-add]');
    if (add) {
      event.preventDefault();
      addRecommendation(add);
    }
  });

  document.addEventListener('change', function (event) {
    if (!enhanced) return;
    var note = closestFrom(event.target, '[data-petz-cart-note]');
    if (note) saveNote(note);
  });

  document.addEventListener('input', function (event) {
    if (!enhanced) return;
    var input = closestFrom(event.target, '[data-petz-qty-input]');
    if (!input) return;

    var line = closestFrom(input, '[data-petz-line]');
    if (!line) return;

    var lineId = line.getAttribute('data-line-id');
    clearDebounce(lineId);
    debounceTimers[lineId] = window.setTimeout(function () {
      delete debounceTimers[lineId];
      commitInput(input);
    }, DEBOUNCE_MS);
  });

  // Enter must not fall through to the native form submit once the automatic
  // path is live, otherwise the page would reload mid-edit.
  document.addEventListener('keydown', function (event) {
    if (!enhanced || event.key !== 'Enter') return;

    var qty = closestFrom(event.target, '[data-petz-qty-input]');
    if (qty) {
      event.preventDefault();
      commitInput(qty);
      return;
    }

    var discount = closestFrom(event.target, '[data-petz-discount-input]');
    if (discount) {
      event.preventDefault();
      applyDiscount(discount.value);
    }
  });

  // ─── Initialisation ──────────────────────────────────────────────────────────

  function init() {
    if (enhanced) return true;
    if (!root()) return false;
    if (!window.Shopify || !window.Shopify.actions) return false;

    var updateCart = window.Shopify.actions.updateCart;
    if (!updateCart || typeof updateCart.configure !== 'function') return false;

    // Configuring replaces Shopify's default page reload with an in-place
    // re-render. If another owner configured first, configure returns false and
    // the Cart stays on its native server-rendered form rather than competing.
    var configured = updateCart.configure({
      eventTarget: function () {
        return root();
      },
      handler: function (defaultHandler) {
        return Promise.resolve(defaultHandler()).then(function (result) {
          return refreshSections().then(
            function () {
              return result;
            },
            function () {
              return result;
            }
          );
        });
      }
    });

    if (configured === false) return false;

    enhanced = true;
    markEnhanced();
    seedDiscountCodes();
    loadRecommendations();
    return true;
  }

  // shopify.dev: "Actions are ready after DOMContentLoaded fires." This file is
  // loaded with defer, so it executes while readyState is already "interactive" -
  // before that event - and must wait for it rather than running immediately.
  // A single "load" retry covers the case where the runtime arrives late.
  function start() {
    if (init()) return;
    window.addEventListener('load', init, { once: true });
  }

  if (document.readyState === 'complete') {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  }

  // Theme editor re-renders the section without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target || !event.target.querySelector) return;
    if (!event.target.querySelector(ROOT_SEL)) return;
    if (enhanced) {
      markEnhanced();
      loadRecommendations();
    } else {
      init();
    }
  });
})();
